import { Base64 } from 'js-base64';
import { isString } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import concat from 'lodash/concat';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import has from 'lodash/has';
import includes from 'lodash/includes';
import indexOf from 'lodash/indexOf';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import merge from 'lodash/merge';
import orderBy from 'lodash/orderBy';
import pick from 'lodash/pick';
import pullAll from 'lodash/pullAll';
import reduce from 'lodash/reduce';
import without from 'lodash/without';
import { TransformType } from 'new/components/ReusableTransform/types';
import {
  DELETE_ATTR_TRANSFORM_SUCCESS,
  DELETE_RECORD_TRANSFORM_SUCCESS,
  DELETE_CODE_CONTAINER_SUCCESS,
  GET_ATTR_TRANSFORMS_FAILURE,
  GET_ATTR_TRANSFORMS_SUCCESS,
  GET_CODE_CONTAINERS_SUCCESS,
  GET_CODE_CONTAINER_LISTING_SUCCESS,
  GET_PUBLIC_ATTR_TRANSFORMS_SUCCESS,
  GET_PUBLIC_CODE_CONTAINERS_SUCCESS,
  GET_PUBLIC_REUSABLE_RECORD_TRANSFORMS_SUCCESS,
  GET_REUSABLE_RECORD_TRANSFORMS_FAILURE,
  GET_REUSABLE_RECORD_TRANSFORMS_SUCCESS,
  GET_TRANSFORM_DOC_SUCCESS,
  GET_TRANSFORM_FEATURES_SUCCESS,
  LOGOUT_COMPLETE,
  SET_LISTING_TRANSFORM_TYPE,
  UPDATE_ATTR_TRANSFORM_SUCCESS,
  UPDATE_CODE_CONTAINER_SUCCESS,
  GET_PIPELINES_SUCCESS,
  UPDATE_RECORD_TX_REUSABILITY_SUCCESS,
  UPDATE_TRANSFORM_DOC_SUCCESS,
  GET_CODE_CONTAINER_SUCCESS,
} from '../../constants/actionTypes';
import { GROUPED_TRANSFORM_RULES } from '../../constants/transforms';
import { convertTransformToCode, decodeTranformCode } from '../../lib/TransformsUtils';
import * as actionTypes from 'js/constants/actionTypes';

const initialState = {
  features: {},
  reusableRecordTransforms: {
    lastUpdated: null,
    lastUpdatedPublic: null,
    sortedIds: [],
    sortedPublicIds: [],
    sortedByNameIds: [],
    sortedByNamePublicIds: [],
    decoded: {},
  },
  attributeTransforms: {
    lastUpdated: null,
    lastUpdatedPublic: null,
    sortedIds: [],
    sortedPublicIds: [],
    sortedByNameIds: [],
    sortedByNamePublicIds: [],
    decoded: {},
  },
  allTransforms: {
    sortedIds: [],
    sortedPublicIds: [],
    sortedByNameIds: [],
    sortedByNamePublicIds: [],
    decoded: {},
  },
  docById: {},
  sortValue: {},
  listing: [],
  listingTransformType: TransformType.ATTRIBUTE,
  groupedTransformRules: GROUPED_TRANSFORM_RULES,
  chunkers: [],
};

export const transformsReducer = (state = initialState, action) => {
  switch (action.type) {
    case GET_REUSABLE_RECORD_TRANSFORMS_SUCCESS:
    case GET_PIPELINES_SUCCESS:
      return saveReusableRecordTransforms(state, action);

    case GET_REUSABLE_RECORD_TRANSFORMS_FAILURE:
      return reusableRecordTransformsFailure(state, action);

    case GET_PUBLIC_REUSABLE_RECORD_TRANSFORMS_SUCCESS:
      return savePublicReusableRecordTransforms(state, action);

    case UPDATE_RECORD_TX_REUSABILITY_SUCCESS:
      return updateRecordTransformReusability(state, action);

    case GET_TRANSFORM_FEATURES_SUCCESS:
      let { result } = action;
      let features = typeof result.features === 'object' ? result.features : {};
      return { ...state, features };

    case GET_ATTR_TRANSFORMS_SUCCESS:
      return saveAttributeTransforms(state, action);

    case GET_ATTR_TRANSFORMS_FAILURE:
      return attributeTransformsFailure(state, action);

    case GET_PUBLIC_ATTR_TRANSFORMS_SUCCESS:
      return savePublicAttributeTransforms(state, action);

    case GET_CODE_CONTAINERS_SUCCESS:
    case GET_PUBLIC_CODE_CONTAINERS_SUCCESS:
      return saveCodeContainers(state, action);

    case GET_CODE_CONTAINER_SUCCESS:
      return saveCodeContainer(state, action);

    case GET_CODE_CONTAINER_LISTING_SUCCESS:
      return saveCodeContainerListing(state, action);

    case UPDATE_ATTR_TRANSFORM_SUCCESS:
      return updateAttributeTransform(state, action);

    case DELETE_ATTR_TRANSFORM_SUCCESS:
      return deleteAttrTx(state, action);

    case UPDATE_CODE_CONTAINER_SUCCESS:
      return updateCodeContainer(state, action);

    case DELETE_RECORD_TRANSFORM_SUCCESS:
    case DELETE_CODE_CONTAINER_SUCCESS:
      return deleteRecordTx(state, action);

    case GET_TRANSFORM_DOC_SUCCESS:
    case UPDATE_TRANSFORM_DOC_SUCCESS:
      return saveTransformDoc(state, action);

    case SET_LISTING_TRANSFORM_TYPE:
      return setListingTransformType(state, action);

    case LOGOUT_COMPLETE:
      return initialState;

    case actionTypes.GET_CODE_CONTAINERS_AI_FUNCTIONS_SUCCESS:
      return getAllReusableTransformAi(state, action);

    default:
      return state;
  }
};

export default transformsReducer;

function saveReusableRecordTransforms(state, action, sort = true) {
  const {
    entities: { transforms },
  } = action;
  let sortedEntities = sort ? orderBy(transforms, ['created_at'], ['desc']) : transforms;
  let sortedIds = map(sortedEntities, 'id');
  let sortedByNameEntities = sort ? orderBy(transforms, ['name']) : transforms;
  let sortedByNameIds = map(sortedByNameEntities, 'id');
  let sortValue = {
    created_at: mapValues(transforms, 'created_at'),
    name: mapValues(transforms, 'name'),
  };

  let decodedTx = reduce(
    transforms,
    (acc, tx, txId) => {
      const { code_encoding, code_type, code } = tx;
      let decoded;
      let codeLanguage;

      if (tx.output_type === TransformType.CUSTOM && isString(code) && code_encoding === 'base64') {
        decoded = Base64.decode(code);
        codeLanguage = code_type;
      } else {
        const result = convertTransformToCode(code);
        decoded = result.code;
        codeLanguage = result.codeLanguage;
      }

      acc[txId] = {
        decoded,
        codeLanguage,
        code_encoding,
      };
      return acc;
    },
    { ...state.reusableRecordTransforms.decoded },
  );

  const mergedSortedByNameIds = [
    ...new Set([...sortedByNameIds, ...state.reusableRecordTransforms.sortedByNameIds]),
  ].sort();

  const mergedSortedIds = [
    ...new Set([...sortedIds, ...state.reusableRecordTransforms.sortedIds]),
  ].sort();

  const newState = {
    ...state,
    reusableRecordTransforms: {
      ...state.reusableRecordTransforms,
      sortedIds: mergedSortedIds,
      sortedByNameIds: mergedSortedByNameIds,
      lastUpdated: action.receivedAt,
      decoded: decodedTx,
    },
    sortValue: merge({}, state.sortValue, sortValue),
  };

  return {
    ...newState,
    allTransforms: mergeReusableTransforms(newState),
  };
}

function reusableRecordTransformsFailure(state, action) {
  return {
    ...state,
    reusableRecordTransforms: {
      ...state.reusableRecordTransforms,
      lastUpdated: action.receivedAt,
    },
  };
}

function savePublicReusableRecordTransforms(state, action) {
  const {
    entities: { transforms },
  } = action;
  const sortedEntities = orderBy(transforms, ['created_at'], ['desc']);
  const sortedPublicIds = map(sortedEntities, 'id');
  let sortedByNameEntities = orderBy(transforms, ['name']);
  let sortedByNamePublicIds = map(sortedByNameEntities, 'id');
  const decodedTx = reduce(
    transforms,
    (acc, tx, txId) => {
      const { code_encoding, code } = tx;
      const { code: decoded, codeLanguage } = convertTransformToCode(code);

      acc[txId] = {
        decoded,
        codeLanguage,
        code_encoding,
      };
      return acc;
    },
    {},
  );

  const newState = {
    ...state,
    reusableRecordTransforms: {
      ...state.reusableRecordTransforms,
      sortedPublicIds,
      sortedByNamePublicIds,
      sortedIds: pullAll(state.reusableRecordTransforms.sortedIds, sortedPublicIds),
      sortedByNameIds: pullAll(
        state.reusableRecordTransforms.sortedByNameIds,
        sortedByNamePublicIds,
      ),
      lastUpdatedPublic: action.receivedAt,
      decoded: { ...state.reusableRecordTransforms.decoded, ...decodedTx },
    },
  };

  return {
    ...newState,
    allTransforms: mergeReusableTransforms(newState),
  };
}

function mergeReusableTransforms(state) {
  const { reusableRecordTransforms, attributeTransforms, sortValue } = state;
  const concatSortedIds = concat(reusableRecordTransforms.sortedIds, attributeTransforms.sortedIds);
  const concatSortedPublicIds = concat(
    reusableRecordTransforms.sortedPublicIds,
    attributeTransforms.sortedPublicIds,
  );

  return {
    sortedIds: orderBy(concatSortedIds, [(id) => sortValue.created_at[id]], ['desc']),
    sortedPublicIds: orderBy(concatSortedPublicIds, [(id) => sortValue.created_at[id]], ['desc']),
    sortedByNameIds: orderBy(concatSortedIds, [(id) => sortValue.name[id]]),
    sortedByNamePublicIds: orderBy(concatSortedPublicIds, [(id) => sortValue.name[id]]),
    decoded: {
      ...reusableRecordTransforms.decoded,
      ...attributeTransforms.decoded,
    },
  };
}

function updateRecordTransformReusability(state, action) {
  const {
    reusableRecordTransforms: { sortedIds, sortedByNameIds },
  } = state;

  let {
    requestInfo: { id, reusable },
    receivedAt,
  } = action;

  let newSortedIds = [];
  let newSortedByNameIds = [];

  if (reusable === true && !includes(sortedIds, id)) {
    if (indexOf(sortedIds, id) === -1) {
      newSortedIds = [id, ...sortedIds];
      newSortedByNameIds = [id, ...sortedIds];
    } else {
      newSortedIds = [...sortedIds];
      newSortedByNameIds = [...sortedByNameIds];
    }
  } else if (reusable === false) {
    newSortedIds = without(sortedIds, id);
    newSortedByNameIds = without(sortedByNameIds, id);
  }

  return {
    ...state,
    reusableRecordTransforms: {
      sortedIds: newSortedIds,
      sortedByNameIds: newSortedByNameIds,
      lastUpdated: receivedAt,
    },
  };
}

function updateCodeContainer(state, action) {
  const {
    entities: { transforms },
    requestInfo: { id },
  } = action;

  const { code_encoding, code } = transforms[id];

  let { code: thisTxDecoded, codeLanguage } = convertTransformToCode(code);

  let newDecoded = {
    ...state.reusableRecordTransforms.decoded,
    [id]: { decoded: thisTxDecoded, code_encoding, codeLanguage },
  };
  return {
    ...state,
    reusableRecordTransforms: {
      ...state.reusableRecordTransforms,
      decoded: newDecoded,
    },
  };
}

function saveAttributeTransforms(state, action) {
  const {
    entities: { transforms },
  } = action;
  const sortedEntities = orderBy(transforms, ['created_at'], ['desc']);
  const sortedIds = map(sortedEntities, 'id');
  const sortedByNameEntities = orderBy(transforms, ['name']);
  const sortedByNameIds = map(sortedByNameEntities, 'id');
  const sortValue = {
    created_at: mapValues(transforms, 'created_at'),
    name: mapValues(transforms, 'name'),
  };
  const decodedTx = reduce(
    transforms,
    (acc, tx, txId) => {
      const { code_encoding, code } = tx;
      const decoded = !isNil(code_encoding) ? decodeTranformCode(code, code_encoding) : code;
      acc[txId] = {
        decoded,
        code_encoding,
      };
      return acc;
    },
    {},
  );
  const newState = {
    ...state,
    attributeTransforms: {
      ...state.attributeTransforms,
      sortedIds: pullAll(sortedIds, state.sortedPublicIds),
      sortedByNameIds: pullAll(sortedByNameIds, state.sortedByNamePublicIds),
      lastUpdated: action.receivedAt,
      decoded: { ...state.decoded, ...decodedTx },
    },
    sortValue: merge({}, state.sortValue, sortValue),
  };
  const groupedTransformRules = getGroupedTransformRules(
    newState.attributeTransforms.sortedByNameIds,
    transforms,
    GROUPED_TRANSFORM_RULES,
  );

  return {
    ...newState,
    groupedTransformRules,
    allTransforms: mergeReusableTransforms(newState),
  };
}

function getGroupedTransformRules(sortedIds, transforms, baseRules) {
  const groupedTransformRules = cloneDeep(baseRules);
  const ungroupedIds = filter(
    sortedIds,
    (id) =>
      !includes(groupedTransformRules.groupOrder, get(transforms[id], 'custom_config.ui.group')),
  );

  if (!isEmpty(ungroupedIds)) {
    if (!includes(groupedTransformRules.groupOrder, 'customAttrTxGroup')) {
      groupedTransformRules.groupOrder.push('customAttrTxGroup');
      groupedTransformRules.groupRules.customAttrTxGroup = {
        label: 'Reusable Attribute Transform Code',
        optionsOrder: [],
      };
    }
  }

  if (!isEmpty(sortedIds)) {
    const newRules = {};

    forEach(sortedIds, (id) => {
      const transform = transforms[id];
      const name = `customAttrTx_${id}`;
      const txGroup = get(transforms[id], 'custom_config.ui.group');
      const group = includes(groupedTransformRules.groupOrder, txGroup)
        ? txGroup
        : 'customAttrTxGroup';
      const { optionsOrder } = groupedTransformRules.groupRules[group];

      if (!includes(optionsOrder, name)) {
        optionsOrder.push(name);
      }

      newRules[name] = {
        value: name,
        label: `${transform.name} (ID: ${id})`,
        group,
        ignoreSrc: true,
        attrTxId: id,
        help: {
          desc: 'Attribute Transform To Use',
          ex: '',
        },
        requiredParams: ['attrTxId'],
        params: [
          {
            name: 'attrTxId',
            typesAllowed: ['number'],
            inputElement: 'attrTxSelector',
            type: 'number',
          },
        ],
      };
    });

    groupedTransformRules.rules = {
      ...groupedTransformRules.rules,
      ...newRules,
    };
  }

  return groupedTransformRules;
}

function attributeTransformsFailure(state, action) {
  return {
    ...state,
    attributeTransforms: {
      ...state.attributeTransforms,
      lastUpdated: action.receivedAt,
    },
  };
}

function savePublicAttributeTransforms(state, action) {
  const {
    entities: { transforms },
  } = action;
  const sortedEntities = orderBy(transforms, ['created_at'], ['desc']);
  const sortedPublicIds = map(sortedEntities, 'id');
  const sortedByNameEntities = orderBy(transforms, ['name']);
  const sortedByNamePublicIds = map(sortedByNameEntities, 'id');
  const decodedTx = reduce(
    transforms,
    (acc, tx, txId) => {
      const { code_encoding, code } = tx;
      const decoded = !isNil(code_encoding) ? decodeTranformCode(code, code_encoding) : code;
      acc[txId] = {
        decoded,
        code_encoding,
      };
      return acc;
    },
    {},
  );
  const newState = {
    ...state,
    attributeTransforms: {
      ...state.attributeTransforms,
      sortedIds: pullAll(state.attributeTransforms.sortedIds, sortedPublicIds),
      sortedPublicIds,
      sortedByNameIds: pullAll(state.attributeTransforms.sortedByNameIds, sortedByNamePublicIds),
      sortedByNamePublicIds,
      lastUpdatedPublic: action.receivedAt,
      decoded: { ...state.attributeTransforms.decoded, ...decodedTx },
    },
  };
  const groupedTransformRules = getGroupedTransformRules(
    newState.attributeTransforms.sortedByNamePublicIds,
    transforms,
    newState.groupedTransformRules,
  );

  return {
    ...newState,
    groupedTransformRules,
    allTransforms: mergeReusableTransforms(newState),
  };
}

function saveCodeContainers(state, action, sort = true) {
  const { type, result, entities } = action;
  const recordTransformIds = filter(result, (id) =>
    [TransformType.RECORD, TransformType.CUSTOM].includes(entities.transforms[id].output_type),
  );
  const recordTransformEntities = pick(entities.transforms, recordTransformIds);
  const attributeTransformIds = filter(
    result,
    (id) => entities.transforms[id].output_type === TransformType.ATTRIBUTE,
  );
  const attributeTransformEntities = pick(entities.transforms, attributeTransformIds);

  let newState = (
    type === GET_PUBLIC_CODE_CONTAINERS_SUCCESS
      ? savePublicReusableRecordTransforms
      : saveReusableRecordTransforms
  )(
    state,
    {
      ...action,
      result: recordTransformIds,
      entities: { ...entities, transforms: recordTransformEntities },
    },
    sort,
  );

  newState = (
    type === GET_PUBLIC_CODE_CONTAINERS_SUCCESS
      ? savePublicAttributeTransforms
      : saveAttributeTransforms
  )(
    newState,
    {
      ...action,
      result: attributeTransformIds,
      entities: { ...entities, transforms: attributeTransformEntities },
    },
    sort,
  );

  return newState;
}

function saveCodeContainer(state, action, sort = true) {
  const { result, entities } = action;
  const recordTransformIds = [result];
  const recordTransformEntities = pick(entities.transforms, recordTransformIds);
  const attributeTransformIds = filter(
    Array.isArray(result) ? result : [result],
    (id) => entities.transforms[id].output_type === TransformType.ATTRIBUTE,
  );
  const attributeTransformEntities = pick(entities.transforms, attributeTransformIds);

  const transform = entities.transforms[result];
  let newState = state;
  if (transform.public) {
    if (transform.output_type === TransformType.ATTRIBUTE) {
      return savePublicAttributeTransforms(
        newState,
        {
          ...action,
          result: attributeTransformIds,
          entities: { ...entities, transforms: attributeTransformEntities },
        },
        sort,
      );
    }
    return savePublicReusableRecordTransforms(
      state,
      {
        ...action,
        result: recordTransformIds,
        entities: { ...entities, transforms: recordTransformEntities },
      },
      sort,
    );
  } else {
    if (transform.output_type === TransformType.ATTRIBUTE) {
      return saveAttributeTransforms(
        newState,
        {
          ...action,
          result: attributeTransformIds,
          entities: { ...entities, transforms: attributeTransformEntities },
        },
        sort,
      );
    }
    return saveReusableRecordTransforms(
      state,
      {
        ...action,
        result: recordTransformIds,
        entities: { ...entities, transforms: recordTransformEntities },
      },
      sort,
    );
  }
}

function saveCodeContainerListing(state, action) {
  const { result } = action;

  return {
    ...saveCodeContainers(state, action, false),
    listing: result,
  };
}

function updateAttributeTransform(state, action) {
  const {
    entities: { transforms },
    requestInfo: { id },
  } = action;

  const { code_encoding, code } = transforms[id];
  let thisTxDecoded = !isNil(code_encoding) ? decodeTranformCode(code, code_encoding) : code;
  let newDecoded = {
    ...state.attributeTransforms.decoded,
    [id]: { decoded: thisTxDecoded, code_encoding },
  };
  return {
    ...state,
    attributeTransforms: {
      ...state.attributeTransforms,
      decoded: newDecoded,
    },
  };
}

function deleteAttrTx(state, action) {
  const {
    requestInfo: { id },
    receivedAt,
  } = action;
  const filterFunction = (sId) => sId !== id;
  const newListing = filter(state.listing, filterFunction);
  const newIds = filter(state.attributeTransforms.sortedIds, filterFunction);

  const newByNameIds = filter(state.attributeTransforms.sortedByNameIds, filterFunction);

  const newDecoded = { ...state.attributeTransforms.decoded };
  delete newDecoded[id];

  const newAllIds = filter(state.allTransforms.sortedIds, filterFunction);

  const newAllByNameIds = filter(state.allTransforms.sortedByNameIds, filterFunction);

  const newAllDecoded = { ...state.allTransforms.decoded };
  delete newAllDecoded[id];

  return {
    ...state,
    attributeTransforms: {
      ...state.attributeTransforms,
      sortedIds: newIds,
      sortedByNameIds: newByNameIds,
      lastUpdated: receivedAt,
      decoded: newDecoded,
    },
    allTransforms: {
      ...state.allTransforms,
      sortedIds: newAllIds,
      sortedPublicIds: state.allTransforms.sortedPublicIds,
      sortedByNameIds: newAllByNameIds,
      decoded: newAllDecoded,
    },
    listing: newListing,
  };
}

function deleteRecordTx(state, action) {
  const {
    requestInfo: { id },
    receivedAt,
  } = action;
  const filterFunction = (sId) => sId !== id;
  const newListing = filter(state.listing, filterFunction);
  const newIds = filter(state.reusableRecordTransforms.sortedIds, filterFunction);

  const newByNameIds = filter(state.reusableRecordTransforms.sortedByNameIds, filterFunction);

  const newDecoded = { ...state.reusableRecordTransforms.decoded };
  if (has(newDecoded, id)) {
    delete newDecoded[id];
  }

  const newAllIds = filter(state.allTransforms.sortedIds, filterFunction);

  const newAllByNameIds = filter(state.allTransforms.sortedByNameIds, filterFunction);

  const newAllDecoded = { ...state.allTransforms.decoded };
  if (has(newAllDecoded, id)) {
    delete newAllDecoded[id];
  }

  return {
    ...state,
    reusableRecordTransforms: {
      ...state.reusableRecordTransforms,
      sortedIds: newIds,
      sortedByNameIds: newByNameIds,
      lastUpdated: receivedAt,
      decoded: newDecoded,
    },
    allTransforms: {
      ...state.allTransforms,
      sortedIds: newAllIds,
      sortedPublicIds: state.allTransforms.sortedPublicIds,
      sortedByNameIds: newAllByNameIds,
      decoded: newAllDecoded,
    },
    listing: newListing,
  };
}

function saveTransformDoc(state, action) {
  const {
    result,
    requestInfo: { id },
  } = action;

  return {
    ...state,
    docById: {
      [id]: isEmpty(result) ? null : result[0],
    },
  };
}

function setListingTransformType(state, action) {
  const { transformType } = action;

  return {
    ...state,
    listingTransformType: transformType,
  };
}

function getAllReusableTransformAi(state, action) {
  return {
    ...state,
    chunkers: action.result,
  };
}
